import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useContext,
    useMemo,
    useState,
} from "react";
import { TPrescriptionDetail } from "./PrescriptionProvider";
import { AlertState } from "../components/alert/AlertsWrapper";
import { usePagination } from '../hooks/usePagination';
import { useOrderOperations } from '../hooks/useOrderOperations';

interface OrderListOptions {
    search?: string;
    status?: string;
    target_device?: string;
    date_after?: string;
    date_before?: string;
}

type TProps = {
    children: ReactNode;
};

type TContext = {
    fromDate: string,
    toDate: string,
    selectedDevice: string;
    selectedOrder: TOrder | null;
    limit: number;
    offset: number;
    ordersList: TOrdersList;
    orderDetail: TOrder | null;
    alertMsgs: AlertState | null;
    ordersLoading: boolean;
    dashboardLoading: boolean;
    orderDetailLoading: boolean;
    setFromDate: Dispatch<SetStateAction<string>>,
    setSelectedDevice: any;
    setToDate: Dispatch<SetStateAction<string>>;
    setAlertMessages: Dispatch<SetStateAction<AlertState | null>>;
    setOffset: Dispatch<SetStateAction<number>>;
    setLimit: Dispatch<SetStateAction<number>>;
    setSelectedOrder: Dispatch<SetStateAction<TOrder | null>>;
    getOrderDetail: (id: string) => Promise<void>;
    sendOrder: (id: string, device?: string) => Promise<string | undefined>;
    getOrderDashboard: () => Promise<TOrdersDashboard | undefined>;
    getDeviceSelector: any
    getOrderList: (options?: OrderListOptions) => Promise<void>;
};

export type TOrder = {
    id: string;
    created_stamp: string;
    external_order_id: string;
    order_name: string;
    medical_system: string;
    medical_system_status: string;
    status: string;
    his_prescription_id: string;
    prescription_detail: TPrescriptionDetail;
    target_device: {
        id: number;
        protocol: string;
        name: string;
        model: string;
        manufacturer: string;
        department: string;
    } | string;
    logs: any
};

export type TOrdersList = {
    count: number;
    next?: string | null;
    previous?: string | null;
    results: TOrder[];
    last_updated_stamp?: Date;
};

export type TOrdersDashboard = {
    all_orders_count: number;
    orders_in_done: number;
    orders_in_progress: number;
    orders_rejected: number;
};

const OrderContext = createContext<TContext>({} as TContext);

export const OrderProvider = ({ children }: TProps) => {
    const [selectedOrder, setSelectedOrder] = useState<TOrder | null>(null);
    const [alertMsgs, setAlertMessages] = useState<AlertState | null>(null);
    const { limit, offset, setLimit, setOffset } = usePagination(10);
    const [selectedDevice, setSelectedDevice] = useState<any>(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const {
        ordersList,
        orderDetail,
        orderDetailLoading,
        ordersLoading,
        dashboardLoading,
        getOrderList: fetchOrderList,
        getOrderDetail: fetchOrderDetail,
        getOrderDashboard: fetchOrderDashboard,
        getDeviceSelector: fetchDeviceSelector,
        sendOrder: sendOrderRequest,
    } = useOrderOperations();

    const getOrderList = async ({ search, status, date_after, date_before, target_device }: OrderListOptions = {}) => {
        await fetchOrderList({ search, status, date_after, date_before, target_device }, limit, offset, setAlertMessages);
    };

    const getOrderDetail = async (id: string) => {
        await fetchOrderDetail(id, setAlertMessages);
    };

    const getOrderDashboard = async () => {
        return await fetchOrderDashboard(setAlertMessages);
    };
    const getDeviceSelector = async () => {
        return await fetchDeviceSelector(setAlertMessages);
    };

    const sendOrder = async (id: string, device?: string) => {
        return await sendOrderRequest(id, device, setAlertMessages);
    };

    const contextValue = useMemo(
        () => ({
            ordersList,
            selectedOrder,
            limit,
            offset,
            alertMsgs,
            orderDetail,
            ordersLoading,
            dashboardLoading,
            orderDetailLoading,
            fromDate,
            toDate,
            selectedDevice,
            setSelectedDevice,
            setFromDate,
            setToDate,
            setAlertMessages,
            setOffset,
            setLimit,
            setSelectedOrder,
            sendOrder,
            getOrderDetail,
            getOrderList,
            getOrderDashboard,
            getDeviceSelector,
        }),
        [
            fromDate,
            toDate,
            selectedDevice,
            ordersList,
            selectedOrder,
            limit,
            offset,
            alertMsgs,
            orderDetail,
            ordersLoading,
            dashboardLoading,
            orderDetailLoading,
        ]
    );

    return (
        <OrderContext.Provider value={contextValue}>
            {children}
        </OrderContext.Provider>
    );
};

export const useOrders = () => useContext(OrderContext);
